/**
 * CW-SF-D1 TKT-5 — Closing This Quarter (Team) widget.
 *
 * Same shape as M5's per-user variant; backend drops the OwnerId
 * filter and adds owner_name per row. Fiscal Q from M6 helper.
 */
import { useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface OppItem {
  id: string;
  external_file_id: string;
  name: string;
  web_url: string | null;
  stage: string | null;
  amount: number | null;
  close_date: string | null;
  owner_id: string | null;
  owner_name: string | null;
  days_to_close: number | null;
  overdue: boolean;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: OppItem[];
  total_visible: number | null;
  amount_denied: boolean;
  q_label: string | null;
  q_start: string | null;
  q_end: string | null;
}

const REFRESH_S = 5 * 60;

export function SfTeamClosingThisQuarterWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/team-closing-this-quarter', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--quarter">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.items?.length ?? 0) > 0}
        emptyMessage={`No team opportunities closing in ${data?.q_label ?? 'this quarter'}.`}
      >
        <div className="sf-widget__header-row">
          <span className="sf-widget__quarter-label">{data?.q_label}</span>
          {data?.total_visible !== null && data?.total_visible !== undefined && (
            <span className="sf-widget__quarter-total">
              {formatCurrency(data.total_visible)}
            </span>
          )}
        </div>
        <ul className="sf-widget__list">
          {data?.items.map((o) => (
            <li
              key={o.id}
              className={
                o.overdue ? 'sf-widget__row sf-widget__row--overdue' : 'sf-widget__row'
              }
            >
              <a
                href={o.web_url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="sf-widget__row-name"
              >
                {o.name}
                <ExternalLink size={10} />
              </a>
              <div className="sf-widget__row-chips">
                {o.stage && <span className="sf-widget__chip">{o.stage}</span>}
                {o.amount !== null && (
                  <span className="sf-widget__chip sf-widget__chip--amount">
                    {formatCurrency(o.amount)}
                  </span>
                )}
                {o.owner_name && (
                  <span className="sf-widget__chip sf-widget__chip--owner">
                    {o.owner_name}
                  </span>
                )}
                {o.close_date && (
                  <span className="sf-widget__chip">{o.close_date}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
